import {
  DatePickerUncontrolled,
  Table,
  DateRangeSelect,
} from "@4uhub/lib4uhub";
import { GridColDef, GridRowParams } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Box, Chip, Stack } from "@mui/material";
import { format } from "date-fns";
import { enUS, ptBR } from "date-fns/locale";
import { IContents } from "../../../../models/content";
import MainApiService from "../../../../services/mainApi.service";
import { useMemo, useState } from "react";

const CONTENT_ROUTE = process.env.REACT_APP_MAIN + "/api/v1/Content/";
const contentService = new MainApiService<IContents>(CONTENT_ROUTE);

const translationPath = "page.register.news.";

const NewsPage = () => {
  const navigate = useNavigate();

  const {
    t,
    i18n: { language },
  } = useTranslation();

  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [enableFields, setEnableFields] = useState<boolean>(true);

  const formatDate = (value: any) => {
    if (!value) return "-";

    return format(new Date(value), "Pp", {
      locale: language === "pt-BR" ? ptBR : enUS,
    }).replaceAll(",", " -");
  };

  const columns: GridColDef<IContents>[] = [
    {
      field: "title",
      headerName: t("components.content.title") || "Title",
      renderCell: (params) => {
        const title = params.row.contents.find(
          (content) => content.language.code === language
        )?.title;
        return title ? title : "-";
      },
    },
    {
      field: "highlightsStart",
      headerName:
        t("components.content.highlight_start") || "Highlight Started At",
      renderCell: ({ value }) => formatDate(value),
    },
    {
      field: "highlightsEnd",
      headerName: t("components.content.highlight_end") || "Highlight Ended At",
      renderCell: ({ value }) => formatDate(value),
    },
    {
      field: "publicationDate",
      headerName:
        t("components.content.publication_date") || "Publication date",
      renderCell: ({ value }) => formatDate(value),
    },
    {
      field: "contentStatus",
      headerName: t("components.content.content_status") || "Content status",
      maxWidth: 150,
      renderCell: (params) => {
        return (
          <Chip
            label={
              params.row.contentStatus.code === "2"
                ? t("components.content.published")
                : t("components.content.not_published")
            }
            color={params.row.contentStatus.code === "2" ? "success" : "error"}
            size="small"
          />
        );
      },
    },
    {
      field: "enable",
      headerName: t("components.content.status") || "Status",
      maxWidth: 150,
      renderCell: (params) => {
        return (
          <Chip
            label={
              params.row.enable
                ? t("components.content.enabled")
                : t("components.content.inactive")
            }
            color={params.row.enable ? "success" : "error"}
            size="small"
          />
        );
      },
    },
  ];

  const handleAdd = () => {
    navigate("new");
  };

  const handleRowClick = (params: GridRowParams<IContents>) => {
    navigate(`${params.id.toString()}/edit`);
  };

  const props = useMemo(() => {
    return {
      StartDate: startDate ? startDate : undefined,
      EndDate: endDate ? endDate : undefined,
      ContentTypeCode: "1",
    };
  }, [startDate, endDate]);
  return (
    <Table<IContents>
      showDefaultMacroFilters={false}
      service={contentService}
      columns={columns}
      title={t(translationPath + "all_news")}
      searchInput
      onAdd={handleAdd}
      onRowClick={handleRowClick}
      serviceProps={props}
      macroFilters={() => {
        return (
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box sx={{ flex: "0 0 200px" }}>
              <DateRangeSelect
                setEndDate={setEndDate}
                setStartDate={setStartDate}
                setEnableFields={setEnableFields}
              />
            </Box>

            <Box sx={{ flex: "0 0 150px" }}>
              <DatePickerUncontrolled
                value={startDate}
                onChange={setStartDate}
                label={`${t(translationPath + "start_date")}`}
                isClearable={false}
                disable={enableFields}
              />
            </Box>
            <Box sx={{ flex: "0 0 150px" }}>
              <DatePickerUncontrolled
                value={endDate}
                onChange={setEndDate}
                label={`${t(translationPath + "end_date")}`}
                isClearable={false}
                disable={enableFields}
              />
            </Box>
          </Stack>
        );
      }}
      searchInputPlaceHolder={
        t(translationPath + "search") || "Search for a news"
      }
      addButtonLabel={t(translationPath + "add_news") || "Add News"}
      confirmOptions={{
        title: t(translationPath + "confirm.title"),
        description: t(translationPath + "confirm.description"),
        confirmationButtonProps: {
          variant: "contained",
        },
      }}
    />
  );
};

export default NewsPage;
